/**
 * G18 model profiles: what each model is good and bad at, so the agent that plans or triages work
 * can pick a model per task with a reason (user, 2026-10-09: "leverage the model's understanding
 * of the strengths/weaknesses of each model"). Rules elsewhere are only a fallback for tasks no
 * model assigned.
 *
 * Tiers are agent-neutral (`fast | standard | deep`). Each profile maps a tier to the id passed to
 * the CLI. Claude uses the CLI aliases (`haiku`/`sonnet`/`opus`), which track the newest model of
 * each family; the concrete ids are kept for display and pricing. Codex leaves the model to its
 * own default and varies effort only, until a project overrides it (`execution.models`).
 *
 * Bump `MODEL_PROFILES_VERSION` whenever a profile's guidance changes, so rendered briefs and
 * managed instruction blocks can be detected as stale.
 */
import { z } from 'zod';
export const MODEL_PROFILES_VERSION = 1;
export const ModelTier = z.enum(['fast', 'standard', 'deep']);
export const MODEL_TIERS = ['fast', 'standard', 'deep'];
/**
 * A model id or alias as passed to a CLI (`--model <id>`): no leading dash, no whitespace, so an
 * agent-supplied value can never become a flag.
 */
export const ModelId = z
    .string()
    .max(80)
    .regex(/^[A-Za-z0-9][A-Za-z0-9._:/[\]-]*$/, 'not a model id');
export const Effort = z.enum(['low', 'medium', 'high', 'xhigh', 'max']);
export const MODEL_PROFILES = [
    {
        agent: 'claude',
        tier: 'deep',
        label: 'Opus 5.5',
        model: 'opus',
        modelId: 'claude-opus-5-5',
        effort: 'high',
        relativeCost: 5,
        strengths: [
            'planning and splitting large work; freezing interfaces',
            'novel or risky algorithms, cross-cutting design, concurrency, security',
            'ambiguous or underspecified requirements',
            'reviewing diffs from cheaper models; landing fixes and hard debugging',
        ],
        weaknesses: ['most expensive and slowest; wasted on mechanical or well-specified work'],
    },
    {
        agent: 'claude',
        tier: 'standard',
        label: 'Sonnet 5.5',
        model: 'sonnet',
        modelId: 'claude-sonnet-5-5',
        effort: 'medium',
        relativeCost: 1,
        strengths: [
            'implementation against a frozen interface and a written acceptance test',
            'adapters, UI wiring, endpoints, migrations, refactors with a clear target',
            'writing and fixing tests; most bug fixes with a reproduction',
        ],
        weaknesses: [
            'drifts on vague briefs or when it must invent cross-module design',
            'can miss subtle invariants in risky algorithmic code; get those reviewed',
        ],
    },
    {
        agent: 'claude',
        tier: 'fast',
        label: 'Haiku 5.5',
        model: 'haiku',
        modelId: 'claude-haiku-5-5',
        effort: 'low',
        relativeCost: 0.3,
        strengths: [
            'mechanical work: fixtures, scaffolding, renames, lint and type fixes',
            'running benchmarks, screenshots and builds, and summarising logs',
            'small single-file edits with exact instructions',
        ],
        weaknesses: [
            'weak at multi-file reasoning and at exploring an unfamiliar codebase',
            'fails on underspecified briefs; give it exact files, excerpts and the expected output',
        ],
    },
    {
        agent: 'codex',
        tier: 'deep',
        label: 'Codex (high effort)',
        effort: 'high',
        relativeCost: 3,
        strengths: ['long autonomous goal loops', 'hard debugging with many test iterations'],
        weaknesses: ['slow; reserve for work that needs persistence'],
    },
    {
        agent: 'codex',
        tier: 'standard',
        label: 'Codex',
        effort: 'medium',
        relativeCost: 1,
        strengths: ['image generation', 'well-specified implementation tasks'],
        weaknesses: ['no per-subagent model choice; split work into Viko tasks instead'],
    },
    {
        agent: 'codex',
        tier: 'fast',
        label: 'Codex (low effort)',
        effort: 'low',
        relativeCost: 0.4,
        strengths: ['mechanical edits and command runs'],
        weaknesses: ['same model as standard; saves effort, not capability'],
    },
];
export function profileFor(agent, tier) {
    const p = MODEL_PROFILES.find((m) => m.agent === agent && m.tier === tier);
    if (!p)
        throw new Error(`no model profile for ${agent}/${tier}`);
    return p;
}
/** The tier a CLI model id belongs to, or undefined for an id no profile names. */
export function tierOfModel(agent, model) {
    if (!model)
        return undefined;
    const id = model.toLowerCase();
    for (const p of MODEL_PROFILES) {
        if (p.agent !== agent)
            continue;
        if (p.model && (id === p.model || id.includes(p.model)))
            return p.tier;
        if (p.modelId && id.startsWith(p.modelId))
            return p.tier;
    }
    return undefined;
}
/** One step up the ladder (deep stays deep). */
export function escalateTier(tier) {
    return tier === 'fast' ? 'standard' : 'deep';
}
/**
 * Per-project evidence appended to a profile ("In this project: …"). Computed at an endpoint from
 * encrypted routing outcomes; never sent to the hub in plaintext.
 */
export const ModelEvidence = z.object({
    agent: z.enum(['claude', 'codex']),
    tier: ModelTier,
    /** Task type (G11 `ROUTING_TASK_TYPES`) or a free label such as "Swift UI". */
    taskType: z.string().min(1).max(60),
    tasks: z.number().int().nonnegative(),
    landedFirstTry: z.number().int().nonnegative(),
    escalated: z.number().int().nonnegative(),
    corrected: z.number().int().nonnegative(),
});
function evidenceLine(e) {
    const parts = [`${e.landedFirstTry} of ${e.tasks} ${e.taskType} tasks landed first try`];
    if (e.escalated)
        parts.push(`${e.escalated} escalated`);
    if (e.corrected)
        parts.push(`${e.corrected} corrected by the user`);
    return parts.join(', ');
}
/**
 * Markdown brief for planners: one block per model of the agent, cheapest first, plus project
 * evidence. `compact` drops strengths/weaknesses beyond the first two (preamble use).
 */
export function renderModelProfiles(agent, opts = {}) {
    const take = (xs) => (opts.compact ? xs.slice(0, 2) : xs);
    const lines = [];
    for (const tier of MODEL_TIERS) {
        const p = profileFor(agent, tier);
        const id = p.model ? `\`${p.model}\`` : 'default model';
        lines.push(`- **${p.label}** (${tier}; ${id}, effort ${p.effort}; ~${p.relativeCost}× cost): good at ${take(p.strengths).join('; ')}. Weak: ${take(p.weaknesses).join('; ')}.`);
        const ev = (opts.evidence ?? []).filter((e) => e.agent === agent && e.tier === tier && e.tasks > 0);
        if (ev.length)
            lines.push(`  In this project: ${ev.map(evidenceLine).join('; ')}.`);
    }
    return lines.join('\n');
}
/** The planner/triage instruction for choosing a model per task (triage preamble and the bench). */
export const MODEL_CHOICE_INSTRUCTION = [
    'Model choice: for every task you create or start, pick the cheapest model that will do it',
    'reliably and pass it on viko_task_create as `tier` (fast, standard or deep) with a one-line',
    '`modelReason`. Put risky, ambiguous or cross-cutting work on deep; implementation with a clear',
    'target on standard; mechanical work with exact instructions on fast. If a task is too vague',
    'for a cheaper model, make its acceptance criteria precise or split it rather than upgrading.',
    'The user can override any pick; a pick the user set in the bark routing is binding.',
].join('\n');
//# sourceMappingURL=model-profiles.js.map